<template>
    <div>
        <!-- Category list -->
        <q-table
            @request="onRequest"
            :filter="table.search"
            :rows="categories"
            :columns="table.cols"
            v-model:pagination="table.pagination"
            :rows-per-page-options="[table.pagination.rowsPerPage]"
            :class="$q.screen.gt.lg ? 'category-table' : ''"
            row-key="id"
            binary-state-sort
            flat
        >
            <!-- top bar -->
            <template v-slot:top>
                <!-- title -->
                <div class="col-12">
                    <div class="text-h4 text-weight-medium">
                        {{ $t('Downtime cause categories') }}
                    </div>
                    <div class="page-description">
                        {{
                            $t(
                                'Manage downtime cause categories for {facilityName}.',
                                {
                                    facilityName: facility?.name
                                }
                            )
                        }}
                    </div>
                </div>
                <!-- search bar -->
                <div
                    class="row fit"
                    :class="
                        $q.screen.lt.sm
                            ? ''
                            : 'wrap justify-between items-start content-start'
                    "
                >
                    <q-input
                        v-model="table.search"
                        @clear="removeFilteredCauseCategoryFromStore"
                        :placeholder="$t('Search downtime category')"
                        :class="
                            $q.screen.lt.sm ? 'col-12 q-mb-md' : 'col q-mr-md'
                        "
                        debounce="750"
                        clearable
                        outlined
                        dense
                        @input="setFilteredCauseCategoryInStore"
                        color="accent"
                    >
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </q-input>
                    <!-- "Add category" button -->
                    <q-btn
                        @click="
                            $router.push({
                                name: 'category_create',
                                params: {
                                    facilityId: facility.id
                                }
                            })
                        "
                        :label="$t('Add category')"
                        :disable="$userCannot('create', 'categories')"
                        class="table-header__button"
                        :class="$q.screen.lt.sm ? 'col-12' : ''"
                        color="accent"
                        size="md"
                    ></q-btn>
                </div>
            </template>

            <!-- Planned column -->
            <template v-slot:body-cell-planned="props">
                <q-td :props="props">
                    <q-icon
                        :name="
                            props.row.planned
                                ? 'fas fa-check-circle'
                                : 'fas fa-times-circle'
                        "
                        :color="props.row.planned ? 'positive' : 'negative'"
                    ></q-icon>
                </q-td>
            </template>

            <!-- actions column -->
            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <div class="row q-gutter-md">
                        <q-btn
                            @click="
                                $router.push({
                                    name: 'category_update',
                                    params: {
                                        facilityId: facility.id,
                                        categoryId: props.row.id
                                    },
                                    state: {
                                        name: props.row.name
                                    }
                                })
                            "
                            class="table__action-button"
                            color="accent"
                            icon="fas fa-edit"
                            outline
                        />
                        <div>
                            <q-btn
                                @click="handleRemove(props.row.id)"
                                :disable="$userCannot('delete', 'categories')"
                                :color="
                                    $userCannot('delete', 'categories')
                                        ? 'grey'
                                        : 'negative'
                                "
                                class="table__action-button"
                                icon="fas fa-trash-alt"
                                outline
                            />
                            <q-tooltip
                                v-if="$userCannot('delete', 'categories')"
                            >
                                {{
                                    $t(
                                        'You do not have permission to perform this action.'
                                    )
                                }}
                            </q-tooltip>
                        </div>
                    </div>
                </q-td>
            </template>
        </q-table>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useFiltersStore } from '@stores/filters.js';
import { useFacilitiesStore } from '@stores/facilities';

export default {
    name: 'CategoryList',
    mixins: [BreadcrumbsManager, UpdateURL],
    props: {
        facilityId: {
            type: [String, Number],
            required: true
        }
    },
    async created() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Downtime Causes')
            }
        ]);

        // get the store filtered cause category name
        const storedFilteredCauseCategory = this.getStoredFacilityFilter({
            facilityId: this.facilityId,
            filter: 'categories'
        });

        // sync the component's search value with the stored
        // fitlered cause category string.
        this.table.search = storedFilteredCauseCategory;
        // if there is nothing in the store for filtered cause category,
        // get all the cause categories from the database for this particular
        // asset, otherwise get the cause categories related to the stored
        // filtered.
        if (!this.table.search) {
            this.onRequest({
                pagination: this.table.pagination,
                filter: ''
            });
        } else {
            this.getCategories();
        }
    },
    data() {
        return {
            baseURL: `facilities/${this.facilityId}/categories/`,
            categories: [],
            // table configs
            table: {
                cols: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true,
                        style: 'width: 85%'
                    },
                    {
                        align: 'left',
                        field: 'actions',
                        name: 'actions',
                        label: this.$t('Actions')
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: ''
            }
        };
    },
    computed: {
        ...mapState(useFiltersStore, ['getStoredFacilityFilter']),
        ...mapState(useFacilitiesStore, ['getFacility']),
        /**
         * Get selected facility.
         * @return {Object} - facility.
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Get URL with query parameters.
         * @return {String}
         */
        url() {
            // get current pagination state attributes
            let { descending, page, sortBy } = this.table.pagination;

            // update URL
            return this.updateURL({
                baseURL: this.baseURL,
                params: {
                    facility: this.facilityId,
                    search: this.table.search,
                    ordering: descending ? `-${sortBy}` : `${sortBy}`,
                    page: page
                }
            });
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useFiltersStore, ['setFacilityFilter']),
        /**
         * Fetch data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         * @param {Object} props.pagination
         * @param {string} props.filter
         */
        async onRequest(props) {
            // update pagination object
            this.table.pagination = props.pagination;

            // begin loading
            this.beginLoading(`${this.$t('Loading categories')}...`);

            // clear existing data
            this.categories = [];

            // fetch data from the server
            await this.getCategories();
        },
        /**
         * Get categories from the server.
         */
        async getCategories() {
            await this.$axios
                .get(this.url)
                .then(response => {
                    // push data
                    this.categories.push(...response.data.results);
                    // update total items count
                    this.table.pagination.rowsNumber = response.data.count;
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to load categories. Please try again later.'
                        ),
                        error
                    );
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Handle remove cause.
         * @param {Number} categoryId
         */
        async handleRemove(categoryId) {
            this.$confirmDelete(
                this.$t('Are you sure you want to delete this category?')
            ).onOk(async () => {
                await this.remove(categoryId);
            });
        },
        /**
         * Remove cause.
         *  @param {Number} categoryId
         */
        async remove(categoryId) {
            // begin updating cause
            this.beginLoading(`${this.$t('Deleting category')}...`);
            await this.$axios
                .delete(`${this.baseURL}${categoryId}`)
                .then(response => {
                    this.$notifySuccess(this.$t('Category deleted'));
                    // refresh
                    this.$router.go();
                })
                .catch(error => {
                    if ('protected_error' in error) {
                        this.$notifyError(
                            this.$t(
                                'Unable to delete because the category is assigned to downtime cause(s). Unset category first to delete.'
                            )
                        );
                    } else {
                        this.$notifyError(
                            this.$t(
                                'Failed to remove category. Please try again later.'
                            ),
                            error
                        );
                    }
                })
                .finally(() => {
                    // stop loading
                    this.stopLoading();
                });
        },

        /**
         * Update the filtered cause category for particular facility,
         * in the store.
         * Depends on the value of table.search
         */
        setFilteredCauseCategoryInStore() {
            this.setFacilityFilter({
                facilityId: this.facilityId,
                filter: 'categories',
                value: this.table.search
            });
        },

        /**
         * Remove the filtered cause category of particular facility
         * from the store. And reset the search key of this.table
         * to its inital value.
         */
        removeFilteredCauseCategoryFromStore() {
            this.setFacilityFilter({
                facilityId: this.facilityId,
                filter: 'categories',
                value: ''
            });
            this.table.search = '';
        }
    }
};
</script>

<style lang="scss" scoped>
// align table top with cards
:deep(.q-table__top) {
    padding: 0px;
}
.category-table {
    height: 75vh;
}
</style>
